import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppServices } from "../../hook/services";
import { useWhiteLabelInfo } from "../../context/whitelabel";
import Select from "react-select";

const Home = () => {
  const AppService = useAppServices();
  const [WhiteLabel, Update] = useWhiteLabelInfo();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [languages, setLanguages] = useState(WhiteLabel?.allowLanguages || []);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const getCategories = async () => {
    try {
      const { response } = await AppService.knowledgebase_category.get_location_categories({
        query: `languageId=${WhiteLabel?.languageId}&companyId=${WhiteLabel?.companyId}`,
      });
      setCategories(response?.data || []);
    } catch (error) {
      console.error(error);
      setCategories([]);
    } finally {
    }
  };

  const getArticles = async () => {
    try {
      const { response } = await AppService.knowledgebase_article.get_location_articles({
        query: `languageId=${WhiteLabel?.languageId}&companyId=${WhiteLabel?.companyId}`,
      });
      setArticles(response?.data || []);
    } catch (error) {
      console.error(error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log(WhiteLabel?.languageId);
    getCategories();
    getArticles();
  }, [WhiteLabel?.languageId]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const matchesQuery = (text) =>
    !normalizedQuery || (text || "").toLowerCase().includes(normalizedQuery);

  const getArticleByCategory = (category) => {
    const categoryArticles = articles.filter(
      (a) => a.category === category._id
    );

    const categoryNameMatches = matchesQuery(category.name);
    const visibleArticles = categoryNameMatches
      ? categoryArticles
      : categoryArticles.filter((a) => matchesQuery(a.name));

    return {
      totalArticlesCount: categoryArticles.length,
      matchedArticlesCount: visibleArticles.length,
      articles: visibleArticles.slice(0, 5),
    };
  };

  const filteredCategories = !normalizedQuery
    ? categories
    : categories.filter((cat) => {
        if (matchesQuery(cat.name)) return true;
        return articles.some(
          (a) => a.category === cat._id && matchesQuery(a.name)
        );
      });

  const handleLanguageChange = (languageId) => {
    Update({ languageId: languageId });
  };

  const headerBg = WhiteLabel?.bgColor || "#6c757d";
  const headerTextColor = WhiteLabel?.textColor || "white";

  if (loading) {
    return (
      <div className="kb-page">
        <header className="kb-hero" style={{ background: headerBg }}>
          <div className="container kb-hero-inner text-center">
            <div className="kb-skel kb-skel--light rounded mx-auto" style={{ height: 28, width: 280 }} />
            <div className="kb-skel kb-skel--light rounded mx-auto mt-3" style={{ height: 12, width: 360 }} />
            <div className="kb-skel kb-skel--light rounded mx-auto mt-4" style={{ height: 48, width: "100%", maxWidth: 560, borderRadius: 12 }} />
          </div>
        </header>
        <div className="container kb-section">
          <div className="kb-skel rounded mx-auto mb-4" style={{ height: 20, width: 220 }} />
          <div className="row g-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="kb-card-skel">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="kb-skel rounded" style={{ width: 36, height: 36 }} />
                    <div className="flex-grow-1">
                      <div className="kb-skel rounded" style={{ height: 12, width: "65%" }} />
                      <div className="kb-skel rounded mt-2" style={{ height: 10, width: "40%" }} />
                    </div>
                  </div>
                  <div className="kb-skel rounded mb-2" style={{ height: 10, width: "80%" }} />
                  <div className="kb-skel rounded" style={{ height: 10, width: "60%" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <KbStyles />
      </div>
    );
  }

  return (
    <div className="kb-page">
      {/* Hero */}
      <header className="kb-hero" style={{ background: headerBg }}>
        <div className="container kb-hero-inner text-center">
          <h2 className="kb-hero-title" style={{ color: headerTextColor }}>
            How can we help you?
          </h2>
          <p className="kb-hero-subtitle" style={{ color: headerTextColor }}>
            Search for answers on orders, returns, and account issues.
          </p>

          <div className="kb-hero-search-wrap mx-auto mt-4">
            <span className="kb-hero-search-icon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="kb-hero-search"
              placeholder="Search categories or articles..."
              aria-label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="kb-hero-search-clear"
                onClick={() => setSearchQuery("")}
                title="Clear search"
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {normalizedQuery && (
            <div className="kb-hero-result-line" style={{ color: headerTextColor }}>
              {filteredCategories.length === 0
                ? `No matches for "${searchQuery}"`
                : `Showing results for "${searchQuery}"`}
            </div>
          )}
        </div>
      </header>

      {/* Toolbar (language switcher) */}
      {WhiteLabel?.allowMultipleLanguages && (
        <div className="container kb-toolbar d-flex justify-content-end">
          <div style={{ minWidth: 200 }}>
            <Select
              options={languages}
              defaultValue={languages.find(
                (item) => WhiteLabel.languageId == item.value
              )}
              onChange={(e) => handleLanguageChange(e.value)}
              placeholder="Change language"
              isSearchable
            />
          </div>
        </div>
      )}

      {/* Section */}
      <div className="container kb-section">
        <h3 className="kb-section-title text-center">
          {normalizedQuery ? "Search results" : "Browse topics"}
        </h3>

        {filteredCategories.length === 0 ? (
          <div className="kb-empty d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 480 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h5 className="kb-empty-title">No categories or articles found</h5>
            <p className="kb-empty-text mb-0">
              Try a different search term or clear the search to see all topics.
            </p>
          </div>
        ) : (
          <div className="row g-3">
            {filteredCategories.map((cat) => {
              const { articles, totalArticlesCount, matchedArticlesCount } =
                getArticleByCategory(cat);

              return (
                <div className="col-md-6 col-lg-4" key={cat._id} count={totalArticlesCount}>
                  <div className="kb-card h-100 d-flex flex-column">
                    <div className="kb-card-header d-flex align-items-center gap-3">
                      <span className="kb-card-icon d-inline-flex align-items-center justify-content-center">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      </span>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h6 className="kb-card-title mb-0 text-truncate">{cat.name}</h6>
                        <div className="kb-card-meta">
                          {totalArticlesCount}{" "}
                          {totalArticlesCount === 1 ? "article" : "articles"}
                        </div>
                      </div>
                    </div>

                    <div className="kb-card-body flex-grow-1">
                      {articles.length > 0 ? (
                        <ul className="kb-article-list">
                          {articles.map((sub) => (
                            <li key={sub._id}>
                              <Link
                                to={`./${cat._id}/${sub._id}`}
                                className="kb-article-item d-flex justify-content-between align-items-center"
                              >
                                <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kb-article-icon flex-shrink-0">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="8" y1="13" x2="16" y2="13" />
                                    <line x1="8" y1="17" x2="13" y2="17" />
                                  </svg>
                                  <span className="kb-article-name text-truncate">{sub.name}</span>
                                </div>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="kb-article-chevron flex-shrink-0">
                                  <polyline points="9 18 15 12 9 6" />
                                </svg>
                              </Link>
                            </li>
                          ))}
                          {normalizedQuery && matchedArticlesCount > 5 && (
                            <li className="kb-article-more">
                              +{matchedArticlesCount - 5} more matching article
                              {matchedArticlesCount - 5 === 1 ? "" : "s"}
                            </li>
                          )}
                        </ul>
                      ) : (
                        <p className="kb-card-empty mb-0">
                          {normalizedQuery
                            ? "No matching articles in this category"
                            : "No articles available"}
                        </p>
                      )}
                    </div>

                    <div className="kb-card-footer">
                      <Link to={`./${cat._id}`} className="kb-btn kb-btn--ghost w-100 justify-content-center">
                        View all articles
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ms-1">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 56px 16px 64px;
      position: relative;
    }
    .kb-hero-inner { max-width: 720px; margin: 0 auto; }
    .kb-hero-title {
      font-size: 2rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin: 0 0 10px;
    }
    .kb-hero-subtitle {
      font-size: 1rem;
      opacity: 0.92;
      margin: 0;
    }
    .kb-hero-search-wrap {
      position: relative;
      max-width: 560px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.06);
    }
    .kb-hero-search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
      display: inline-flex;
    }
    .kb-hero-search {
      width: 100%;
      height: 48px;
      border: 0;
      border-radius: 12px;
      padding: 0 48px 0 44px;
      background: #fff;
      color: #1f2937;
      font-size: 0.9375rem;
      outline: none;
      font-family: inherit;
    }
    .kb-hero-search::placeholder { color: #9ca3af; }
    .kb-hero-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 30px;
      height: 30px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-hero-search-clear:hover { background: #f3f4f6; color: #1f2937; }
    .kb-hero-result-line {
      margin-top: 14px;
      font-size: 0.8125rem;
      opacity: 0.92;
    }

    /* Toolbar */
    .kb-toolbar {
      margin-top: 24px;
    }

    /* Section */
    .kb-section { padding: 28px 16px 64px; }
    .kb-section-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.01em;
      margin: 0 0 24px;
    }

    /* Card */
    .kb-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.2s ease;
    }
    .kb-card:hover {
      border-color: #dcdcdc;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 8px 20px rgba(17, 24, 39, 0.06);
    }
    .kb-card-header {
      padding: 14px 16px;
      border-bottom: 1px solid #f1f1f1;
      background: #fafafa;
    }
    .kb-card-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: #eef2ff;
      color: #4f46e5;
      flex-shrink: 0;
    }
    .kb-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-card-meta {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 2px;
    }
    .kb-card-body { padding: 14px 16px 6px; }
    .kb-card-footer { padding: 8px 16px 14px; }
    .kb-card-empty {
      font-size: 0.8125rem;
      color: #9ca3af;
      padding: 6px 0 10px;
    }

    /* Article list inside card */
    .kb-article-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .kb-article-item {
      display: flex;
      padding: 9px 10px;
      border-radius: 7px;
      transition: background 0.12s ease, color 0.12s ease;
      font-size: 0.875rem;
      color: #1f2937;
      text-decoration: none;
    }
    .kb-article-list li + li .kb-article-item { margin-top: 2px; }
    .kb-article-item:hover {
      background: #f5f3ff;
      color: #4338ca;
      text-decoration: none;
    }
    .kb-article-icon {
      color: #9ca3af;
      transition: color 0.12s ease;
    }
    .kb-article-item:hover .kb-article-icon { color: #4f46e5; }
    .kb-article-chevron {
      color: #cbd5e1;
      transition: transform 0.12s ease, color 0.12s ease;
    }
    .kb-article-item:hover .kb-article-chevron {
      color: #4f46e5;
      transform: translateX(2px);
    }
    .kb-article-name { font-weight: 500; }
    .kb-article-more {
      font-size: 0.75rem;
      color: #6b7280;
      padding: 6px 10px;
    }

    /* Buttons */
    .kb-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 14px;
      border-radius: 8px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .kb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      text-decoration: none;
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
      padding: 48px 24px;
    }
    .kb-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }

    /* Skeleton */
    .kb-card-skel {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 16px 18px;
    }
    .kb-skel {
      background: linear-gradient(90deg, #eef2f7 0%, #f3f4f6 50%, #eef2f7 100%);
      background-size: 200% 100%;
      animation: kb-shimmer 1.4s ease-in-out infinite;
    }
    .kb-skel--light {
      background: linear-gradient(90deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.18) 100%);
      background-size: 200% 100%;
    }
    @keyframes kb-shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
  `}</style>
);

export default Home;
